import { translationsMapping } from '../../config'
import types from './types'
import cardTypes from './cardTypes'
import monsterAttributes from './monsterAttributes'
import monsterTypes from './monsterTypes'
import { sortByName } from '../helper/sort'
import rarities from './rarities'
import countries from './countries'
import places from './places'
import effectActions from './effectActions'
import effectPlaces from './effectPlaces'
import cardLanguages from './cardLanguages'
import editions from './editions'
import conditions from './conditions'
import formats, { GENESYS, MASTER_DUEL, OCG, SPEED_DUEL, TCG, TCG_TRADITIONAL } from './formats'
import gradingServices from './gradingServices'
import userTypes from './userTypes'
import { capitalize } from '../helper/string'
import rarityGroups from './rarityGroups'
import communityViolations from './communityViolations'
import contactReasons from './contactReasons'
import priceSources, { cardmarketOptions, TCG_PLAYER, tcgPlayerOptions } from './priceSources'
import reportUserReasons from './reportUserReasons'
import skillCardGroups from './skillCardGroups'
import deckTags, { deckTagGroups } from './deckTags'

export const cardTypesOptions = (languageCode, type = null) => {
	let entries = Object.entries(cardTypes)

	if (type) {
		entries = entries.filter(([key, value]) => value.type === type)
	}

	return entries.map(([key, value]) => ({
		value: key,
		label: value[`name_${languageCode}`]
	}))
}

export const cardTypesOptionsGrouped = (t, numbers, languageCode, filter = false) => [
	...(!numbers || !numbers.find(item => item.id === 'main') ? [{
		value: 'main',
		label: t('general.mainDeck'),
		number: numbers?.find(item => item.id === 'main')?.number || 0
	}] : []),
	...(!numbers || !numbers.find(item => item.id === 'extra') ? [{
		value: 'extra',
		label: t('general.extraDeck'),
		number: numbers?.find(item => item.id === 'extra')?.number || 0
	}] : []),
	...types.map(type => ({
		label: t(type.name),
		options: Object.entries(cardTypes)
			.filter(([key, value]) => {
				if (filter) {
					return value.type === type.value && numbers?.find(item => item.id === Number(key))
				}

				return value.type === type.value
			})
			.map(([key, value]) => {
				let number = null

				const numberData = numbers && numbers.find(item => item.id === Number(key))

				if (numberData) {
					number = numberData.number
				}

				return {
					value: key,
					label: value[`name_${languageCode}`],
					number,
					cardType: Number(key),
					cardCategory: value.type
				}
			})
	}))
]

export const cardTypesSkillOptions = (t, languageCode) => skillCardGroups.map(skillGroup => ({
	label: skillGroup.name.startsWith('general.') ? t(skillGroup.name) : skillGroup.name,
	options: sortByName(Object.entries(cardTypes).filter(([key, value]) => skillGroup.members.find(item => item === Number(key))).map(([key, value]) => ({
		value: key,
		label: value[`name_${languageCode}`]
	})), 'label')
}))

export const artworkCategoryOptions = (t, selectedCategories = null) => ['all', 'alternate-art', 'extended-art', 'lost-art', 'original'].map(category => ({
	value: category,
	label: category === 'all' ? t('general.all') : t(`general.artworkCategory_${category}`),
	number: selectedCategories?.find(item => item.id === category)?.number || 0
}))

export const monsterAttributeOptions = (languageCode, selectedAttributes = null) => Object.entries(monsterAttributes).map(([key, value]) => ({
	value: key,
	label: value[`name_${languageCode}`],
	order: value.order,
	number: selectedAttributes?.find(item => item.id === Number(key))?.number || 0,
	monsterAttribute: key
})).sort((a, b) => a.order - b.order)

export const monsterTypeOptions = (languageCode, selectedTypes = null) => sortByName(Object.entries(monsterTypes).map(([key, value]) => ({
	value: key,
	label: value[`name_${languageCode}`],
	number: selectedTypes?.find(item => item.id === Number(key))?.number || 0,
	monsterType: key
})), 'label')

export const typesOptions = (t, selectTypes = null) => types.map(type => ({
	value: type.value,
	label: t(type.name),
	number: selectTypes?.find(item => item.id === type.value)?.number,
	cardCategory: type.value
}))

export const raritiesOptions = (t, format = TCG) => Object.values(rarityGroups)
	.filter(group => group.format === format)
	.map(group => ({
		label: t(group.name),
		options: group.members.map(id => ({
			value: id,
			label: rarities[id].name,
			abbr: rarities[id].abbr
		}))
	}))

export const filteredRaritiesOptions = (t, data, format = TCG, showAbbr = false) => Object.values(rarityGroups)
	.filter(group => group.format === format)
	.map(group => ({
		label: t(group.name),
		options: group.members.filter(id => !data || data.find(item => item.id === id)).map(id => ({
			value: id,
			label: rarities[id].name,
			number: data?.find(item => item.id === id).number,
			rarity: showAbbr ? id : undefined
		}))
	}))

export const mainMenuEntries = t => [
	{
		name: 'general.spoiler',
		to: '/cards/spoiler'
	},
	{
		name: 'general.limited',
		nameShort: 'general.limitedShort',
		to: '/limited-list/tcg'
	},
	{
		name: 'general.rarities',
		to: '/rarities'
	},
	{
		name: 'general.artworks',
		to: '/artworks'
	},
	{
		name: 'general.ocgOnlyCards',
		to: '/cards/ocgonly'
	},
	{
		name: 'general.masterDuelMissingCards',
		to: '/cards/master-duel-missing-cards'
	}
].map(entry => ({
	...entry,
	name: t(entry.name)
}))

export const countryOptions = language => sortByName(Object.entries(countries).map(([key, value]) => ({
	value: key,
	name: value[`name_${translationsMapping[language]}`]
})), 'name')

export const countrySubscriptionOptions = (supportedCountries, language, t) => ['europe', 'northAmerica', 'southAmerica', 'oceania', 'asia'].map(region => ({
	label: t(`general.region_${region}`),
	options: sortByName(Object.entries(countries).filter(([key, value]) => supportedCountries.find(item => item === key) && value.region === region).map(([key, value]) => ({
		value: key,
		name: value[`name_${translationsMapping[language]}`]
	})), 'name')
})).filter(item => item.options.length > 0)

export const stateOptions = country => sortByName(Object.entries(countries[country].states).map(([key, value]) => ({
	value: key,
	name: value
})), 'name')

export const formatOptions = () => [
	{
		label: formats[TCG].name,
		value: TCG,
		color: formats[TCG].color
	},
	{
		label: formats[OCG].name,
		value: OCG,
		color: formats[OCG].color
	},
	{
		label: formats[GENESYS].name,
		value: GENESYS,
		color: formats[GENESYS].color
	},
	{
		label: formats[MASTER_DUEL].name,
		value: MASTER_DUEL,
		color: formats[MASTER_DUEL].color
	},
	{
		label: formats[SPEED_DUEL].name,
		value: SPEED_DUEL,
		color: formats[SPEED_DUEL].color
	},
	{
		label: formats[TCG_TRADITIONAL].name,
		value: TCG_TRADITIONAL,
		color: formats[TCG_TRADITIONAL].color
	}
]

export const placeOptions = t => Object.entries(places).filter(([key, value]) => !value.disabled).map(([key, value]) => ({
	value: key,
	label: t(value.nameShort)
}))

export const effectActionOptions = language => {
	const translationKey = translationsMapping[language] === 'en' || translationsMapping[language] === 'de' ? translationsMapping[language] : 'en'

	return sortByName(Object.entries(effectActions).map(([key, value]) => ({
		value: value.keywords,
		label: value[`name_${translationKey}`]
	})), 'label', 'name_en')
}

export const effectPlaceOptions = language => {
	const translationKey = translationsMapping[language] === 'en' || translationsMapping[language] === 'de' ? translationsMapping[language] : 'en'

	return sortByName(Object.entries(effectPlaces).map(([key, value]) => ({
		value: value.keywords,
		label: value[`name_${translationKey}`]
	})), 'label', 'name_en')
}

export const effectCardTypeOptions = (language, type = null) => {
	let entries = Object.entries(cardTypes)

	if (type) {
		entries = entries.filter(([key, value]) => value.type === type)
	}

	return entries.map(([key, value]) => ({
		value: value.keywords,
		label: value[`name_${translationsMapping[language]}`]
	}))
}

export const effectMonsterAttributeOptions = language => sortByName(Object.entries(monsterAttributes).map(([key, value]) => ({
	value: value.name_en,
	label: value[`name_${translationsMapping[language]}`]
})), 'label')

export const effectMonsterTypeOptions = language => sortByName(Object.entries(monsterTypes).map(([key, value]) => ({
	value: value.name_en,
	label: value[`name_${translationsMapping[language]}`]
})), 'label')

export const cardLanguageOptionsPlainList = t => sortByName(Object.entries(cardLanguages).filter(([key]) => key !== 'world').map(([key, value]) => ({
	value: key,
	label: t(value.name),
	abbr: value.abbr
})), 'label')

export const cardLanguageOptions = (t) => {
	const defaultLanguages = sortByName(Object.entries(cardLanguages).filter(([key, value]) => key !== 'world' && !value.specificationOf && !value.other).map(([key, value]) => ({
		value: key,
		label: t(value.name)
	})), 'label')

	return [
		...defaultLanguages,
		{
			label: t('general.languageVariants'),
			options: sortByName(Object.entries(cardLanguages).filter(([key, value]) => key !== 'world' && !!value.specificationOf).map(([key, value]) => ({
				value: key,
				label: t(value.name)
			})), 'label')
		},
		{
			label: t('general.other'),
			options: sortByName(Object.entries(cardLanguages).filter(([key, value]) => key !== 'world' && value.other).map(([key, value]) => ({
				value: key,
				label: t(value.name)
			})), 'label')
		}
	]
}

export const editionOptions = t => Object.entries(editions).map(([key, value]) => ({
	value: key,
	label: t(value.name)
}))

export const conditionOptions = (gradingService = null, language = null) => {
	if (gradingService && gradingServices[gradingService] && gradingServices[gradingService].scale) {
		return gradingServices[gradingService].scale.map(item => ({
			value: item.value,
			label: item.nameShort || item.name
		}))
	}

	return Object.entries(conditions).map(([key, value]) => ({
		value: key,
		label: value.name
	})).reverse()
}

export const gradingServiceOptions = () => Object.entries(gradingServices).map(([key, value]) => ({
	value: key,
	label: `${key} (${value.name})`
}))

export const userTypeOptions = t => userTypes.map(item => ({
	value: item,
	label: t(`general.userType${capitalize(item)}`)
}))

export const communityViolationOptions = t => communityViolations.map(item => ({
	value: item.value,
	label: t(item.label)
}))

export const reportUserReasonOptions = t => reportUserReasons.map(item => ({
	value: item.value,
	label: t(item.label)
}))

export const contactReasonOptions = t => contactReasons.map(item => ({
	value: item.value,
	label: t(item.label)
}))

export const priceSourceOptions = priceSourceDefault => Object.entries(priceSources).map(([key, value]) => ({
	label: value.name,
	value: Number(key),
	...value,
	options: Number(key) === TCG_PLAYER ? tcgPlayerOptions : cardmarketOptions
})).sort((a, b) => {
	if (Number(a.value) === priceSourceDefault && Number(b.value) !== priceSourceDefault) return -1
	if (Number(a.value) !== priceSourceDefault && Number(b.value) === priceSourceDefault) return 1

	return 0
})

export const deckTagOptions = (t, userLanguage) => deckTagGroups.map(group => ({
	label: t(group.name),
	options: deckTags.filter(tag => group.members.includes(tag.id)).sort((a, b) => group.members.indexOf(a.id) - group.members.indexOf(b.id)).map(tag => ({
		value: tag.id,
		label: tag[`name_${translationsMapping[userLanguage]}`],
		slug: tag.slug
	}))
}))
